<template>
  <section v-if="documents.length" class="section-block">
    <h2><i class="fas fa-file-shield"></i> Policies on file</h2>
    <p class="section-hint">
      Latest comprehensive check for each certificate. Limits, dates, and the requirement results are what that check read off the policy. A gap can be waived with a reason, the same waiver the compliance agent saves for this insured.
    </p>

    <article v-for="doc in documents" :key="doc.check_run_id" class="policy-doc">
      <header class="policy-doc-header">
        <strong>{{ doc.document_name || 'Certificate' }}</strong>
        <span v-if="doc.checked_at" class="checked-at">Checked {{ formatDate(doc.checked_at) }}</span>
      </header>

      <div v-for="policy in doc.policies" :key="policy.coverage_type" class="policy-card">
        <div class="policy-title-row">
          <h3>{{ policy.coverage_label }}</h3>
          <span class="policy-score">
            {{ policy.passed_count }} passed
            <template v-if="policy.waived_count"> · {{ policy.waived_count }} waived</template>
            · {{ policy.failed_count }} failed
          </span>
        </div>
        <dl class="policy-facts">
          <div v-if="policy.policy_number">
            <dt>Policy number</dt>
            <dd>{{ policy.policy_number }}</dd>
          </div>
          <div v-if="policy.effective_date">
            <dt>Effective</dt>
            <dd>{{ formatDate(policy.effective_date) }}</dd>
          </div>
          <div v-if="policy.expiration_date">
            <dt>Expires</dt>
            <dd>{{ formatDate(policy.expiration_date) }}</dd>
          </div>
        </dl>
        <div v-if="policy.limits.length" class="limits">
          <h4>Limits</h4>
          <ul>
            <li v-for="limit in policy.limits" :key="limit.label + limit.amount">
              <span>{{ limit.label }}</span>
              <strong>{{ limit.amount }}</strong>
            </li>
          </ul>
        </div>
        <EntityPolicyCheckList
          :checks="policy.checks"
          :subject-id="subjectId"
          @waived="$emit('waived')"
        />
      </div>

      <div v-if="doc.also_checked && doc.also_checked.length" class="certificate-checks">
        <h4>Checked, not on this certificate</h4>
        <EntityPolicyCheckList
          :checks="doc.also_checked"
          :subject-id="subjectId"
          @waived="$emit('waived')"
        />
      </div>

      <div v-if="doc.certificate_checks.length" class="certificate-checks">
        <h4>Certificate-wide results</h4>
        <EntityPolicyCheckList
          :checks="doc.certificate_checks"
          :subject-id="subjectId"
          @waived="$emit('waived')"
        />
      </div>
    </article>
  </section>
</template>

<script>
import EntityPolicyCheckList from '@/components/compliance/EntityPolicyCheckList.vue';

export default {
  name: 'EntityPolicyInventory',
  components: { EntityPolicyCheckList },
  props: {
    documents: { type: Array, default: () => [] },
    subjectId: { type: [String, Number], required: true },
  },
  emits: ['waived'],
  setup() {
    const formatDate = (value) => {
      if (!value) return '';
      const parsed = new Date(`${value}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) return value;
      return parsed.toLocaleDateString();
    };
    return { formatDate };
  },
};
</script>

<style scoped>
.section-hint {
  color: #666;
  font-size: 0.85rem;
  margin: 0 0 1rem;
}

.policy-doc {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 1rem;
  overflow: hidden;
  background: #fff;
}

.policy-doc-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0.9rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.checked-at {
  color: #64748b;
  font-size: 0.8rem;
}

.policy-card,
.certificate-checks {
  padding: 0.85rem 0.9rem;
  border-top: 1px solid #eef2f7;
}

.policy-title-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: baseline;
}

.policy-title-row h3,
.limits h4,
.certificate-checks h4 {
  margin: 0;
  font-size: 1rem;
}

.policy-score {
  color: #475569;
  font-size: 0.8rem;
  font-weight: 600;
}

.policy-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 0.6rem 0 0;
}

.policy-facts div {
  min-width: 8rem;
}

.policy-facts dt {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.policy-facts dd {
  margin: 0.15rem 0 0;
  font-weight: 600;
}

.limits ul {
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
}

.limits li {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.85rem;
}
</style>
